import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Hammer, Clock, Calendar, MessageSquare, FileText, ExternalLink, Paperclip, Download, Eye, Upload, Camera, Link, FolderOpen, ClipboardList, CalendarDays, LayoutDashboard } from 'lucide-react';
import PunchlistTab from '../components/PunchlistTab';
import CalendarTab from '../components/CalendarTab';
import ScheduleTab from '../components/ScheduleTab';
import ProjectOverviewTab from '../components/ProjectOverviewTab';
import ChangeOrdersTab from '../components/ChangeOrdersTab';

const API_URL = '';

export default function ConstructionDepartment({ user, onLogout, theme, onThemeToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    const stateTab = location.state?.activeTab;
    if (stateTab === 'calendar') return 'calendar';
    if (stateTab === 'rfi') return 'rfi';
    if (stateTab === 'punchlist') return 'punchlist';
    if (stateTab === 'dtr') return 'dtr';
    return 'overview';
  });
  const [dtrEntries, setDtrEntries] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [rfis, setRfis] = useState([]);
  const [projectSchedules, setProjectSchedules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [dprs, setDprs] = useState([]);
  const [dtrForm, setDtrForm] = useState({ 
    project: '', 
    date: '', 
    early_in: '', 
    early_out: '', 
    time_in: '08:00', 
    time_out: '17:00', 
    break_hours: '1',
    overtime_in: '',
    overtime_out: '',
    ot_code: '',
    work_description: '' 
  });
  const [otCodeVerified, setOtCodeVerified] = useState(false);
  const [otCodeMessage, setOtCodeMessage] = useState('');
  const [verifyingOtCode, setVerifyingOtCode] = useState(false);
  const [approvedOtHours, setApprovedOtHours] = useState(0);
  const [myOtApps, setMyOtApps] = useState([]);
  const [showPrevOt, setShowPrevOt] = useState(false);
  const [reimbForm, setReimbForm] = useState({ amount: '', description: '', receipt: null, receipt_url: '', project_id: '', project_name: '', date: new Date().toISOString().split('T')[0] });
  const [rfiForm, setRfiForm] = useState({ title: '', description: '', project: '', attachment: null });
  const [dprForm, setDprForm] = useState({ 
    project: '', 
    accomplished_works: '', 
    site_photos: [],
    challenges: '' 
  });
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [submittingDpr, setSubmittingDpr] = useState(false);
  const [selectedDpr, setSelectedDpr] = useState(null);
  const [dprProjectFilter, setDprProjectFilter] = useState('');
  const [constructionReferences, setConstructionReferences] = useState([]);
  const [refProjectFilter, setRefProjectFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    axios.get(`${API_URL}/api/dtr`).then(res => setDtrEntries(res.data));
    axios.get(`${API_URL}/api/ot/my`).then(res => setMyOtApps(res.data)).catch(() => {});
    axios.get(`${API_URL}/api/reimbursements`).then(res => setReimbursements(res.data));
    axios.get(`${API_URL}/api/rfi`).then(res => setRfis(res.data.filter(r => r.type === 'submission')));
    axios.get(`${API_URL}/api/project-schedules?department=construction`).then(res => setProjectSchedules(res.data));
    axios.get(`${API_URL}/api/projects`).then(res => setProjects(res.data));
    axios.get(`${API_URL}/api/users/list`).then(res => setUsers(res.data)).catch(err => console.error('Failed to load users:', err));
    axios.get(`${API_URL}/api/dpr`).then(res => setDprs(res.data));
    axios.get(`${API_URL}/api/construction-references`).then(res => setConstructionReferences(res.data)).catch(err => console.error('Failed to load references:', err));
  };

  // Helper function to convert time string to minutes
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Calculate early work hours (12:00 AM - 7:59 AM, min 1 hour)
  const calculateEarlyHours = () => {
    if (!dtrForm.early_in || !dtrForm.early_out) return 0;
    const earlyIn = timeToMinutes(dtrForm.early_in);
    const earlyOut = timeToMinutes(dtrForm.early_out);
    if (earlyIn > 479 || earlyOut > 479) return 0;
    const minutes = earlyOut - earlyIn;
    const hours = minutes / 60;
    return hours >= 1 ? hours : 0;
  };

  // Calculate regular work hours (8:00 AM - 5:00 PM)
  const calculateRegularHours = () => {
    if (!dtrForm.time_in || !dtrForm.time_out) return 0;
    const timeIn = timeToMinutes(dtrForm.time_in);
    const timeOut = timeToMinutes(dtrForm.time_out);
    const regularStart = Math.max(timeIn, 480);
    const regularEnd = Math.min(timeOut, 1020);
    if (regularEnd <= regularStart) return 0;
    return (regularEnd - regularStart) / 60;
  };

  // Calculate overtime hours (5:01 PM - 11:59 PM, min 2 hours)
  const calculateOvertimeHours = () => {
    if (!dtrForm.overtime_in || !dtrForm.overtime_out) return 0;
    const otIn = timeToMinutes(dtrForm.overtime_in);
    const otOut = timeToMinutes(dtrForm.overtime_out);
    if (otIn < 1021 || otOut < 1021) return 0;
    if (otOut > 1439) return 0;
    const hours = (otOut - otIn) / 60;
    return hours >= 2 ? hours : 0;
  };

  // Calculate total work hours (Early + Regular - Break)
  const calculateTotalWorkHours = () => {
    const earlyHours = calculateEarlyHours();
    const regularHours = calculateRegularHours();
    const breakHours = parseFloat(dtrForm.break_hours) || 0;
    return Math.max(0, earlyHours + regularHours - breakHours);
  };

  // Verify OT code
  const verifyOtCode = async () => {
    if (!dtrForm.ot_code.trim()) {
      setOtCodeMessage('Please enter an OT code');
      setOtCodeVerified(false);
      return;
    }
    
    if (!dtrForm.date) {
      setOtCodeMessage('Please select a date first');
      setOtCodeVerified(false);
      return;
    }
    
    setVerifyingOtCode(true);
    try {
      const formData = new FormData();
      formData.append('ot_code', dtrForm.ot_code.trim());
      formData.append('date', dtrForm.date);
      
      const res = await axios.post(`${API_URL}/api/ot/verify-code`, formData);
      
      if (res.data.valid) {
        setOtCodeVerified(true);
        setApprovedOtHours(res.data.hours);
        setOtCodeMessage(`✓ Verified for ${res.data.hours} hour(s) on ${res.data.date}`);
        toast.success('OT code verified!');
      } else {
        setOtCodeVerified(false);
        setApprovedOtHours(0);
        setOtCodeMessage(res.data.message);
      }
    } catch (error) {
      setOtCodeVerified(false);
      setApprovedOtHours(0);
      setOtCodeMessage('Failed to verify OT code. Please try again.');
      toast.error('Failed to verify OT code');
    } finally {
      setVerifyingOtCode(false);
    }
  };

  const selectPrevOt = (ot) => {
    setDtrForm(prev => ({ ...prev, ot_code: ot.ot_code, date: ot.date }));
    setOtCodeVerified(false);
    setOtCodeMessage('');
    setShowPrevOt(false);
    toast.info(`Loaded ${ot.ot_code} — click Verify to apply`);
  };

  // Auto-compute overtime end based on approved hours
  const handleOvertimeStartChange = (startTime) => {
    setDtrForm(prev => {
      const newForm = { ...prev, overtime_in: startTime };
      
      // Auto-compute end time if we have approved hours
      if (startTime && approvedOtHours > 0) {
        const [hours, minutes] = startTime.split(':').map(Number);
        const startMinutes = hours * 60 + minutes;
        const endMinutes = startMinutes + (approvedOtHours * 60);
        
        // Cap at 23:59
        const cappedEndMinutes = Math.min(endMinutes, 23 * 60 + 59);
        const endHours = Math.floor(cappedEndMinutes / 60);
        const endMins = cappedEndMinutes % 60;
        
        newForm.overtime_out = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
      }
      
      return newForm;
    });
  };

  // Reset OT code verification when date changes
  const handleDateChange = (newDate) => {
    setDtrForm({ ...dtrForm, date: newDate });
    if (dtrForm.ot_code) {
      setOtCodeVerified(false);
      setApprovedOtHours(0);
      setOtCodeMessage('Date changed. Please verify OT code again.');
    }
  };

  const handleDtrSubmit = async (e) => {
    e.preventDefault();
    const totalWorkHours = calculateTotalWorkHours();
    const overtimeHours = calculateOvertimeHours();
    const earlyHours = calculateEarlyHours();
    
    if (totalWorkHours <= 0 && overtimeHours <= 0) {
      toast.error('Please enter valid work hours');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/dtr`, {
        project: dtrForm.project,
        date: dtrForm.date,
        hours: totalWorkHours.toFixed(2),
        time_in: dtrForm.time_in,
        time_out: dtrForm.time_out,
        early_in: dtrForm.early_in || null,
        early_out: dtrForm.early_out || null,
        early_hours: earlyHours.toFixed(2),
        break_hours: dtrForm.break_hours,
        overtime_in: dtrForm.overtime_in || null,
        overtime_out: dtrForm.overtime_out || null,
        overtime_hours: overtimeHours.toFixed(2),
        work_description: dtrForm.work_description
      }, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      toast.success('DTR entry logged!');
      setDtrForm({ 
        project: '', 
        date: '', 
        early_in: '', 
        early_out: '', 
        time_in: '08:00', 
        time_out: '17:00', 
        break_hours: '1',
        overtime_in: '',
        overtime_out: '',
        ot_code: '',
        work_description: '' 
      });
      setOtCodeVerified(false);
      setOtCodeMessage('');
      setApprovedOtHours(0);
      loadData();
    } catch (error) {
      toast.error('Failed to log DTR');
    }
  };

  const handleReimbursementSubmit = async (e) => {
    e.preventDefault();
    if (!reimbForm.receipt) {
      toast.error('Please select a receipt file first');
      return;
    }
    if (!reimbForm.project_id) {
      toast.error('Please select a project');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('amount', reimbForm.amount);
      formData.append('description', reimbForm.description);
      formData.append('receipt', reimbForm.receipt);
      formData.append('project_id', reimbForm.project_id);
      formData.append('project_name', reimbForm.project_name);
      formData.append('date', reimbForm.date);
      
      await axios.post(`${API_URL}/api/reimbursements`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Reimbursement request submitted!');
      setReimbForm({ amount: '', description: '', receipt: null, receipt_url: '', project_id: '', project_name: '', date: new Date().toISOString().split('T')[0] });
      // Reset file input
      const fileInput = document.getElementById('receipt');
      if (fileInput) fileInput.value = '';
      loadData();
    } catch (error) {
      toast.error('Failed to submit reimbursement');
    }
  };

  const handleReceiptSelect = (file) => {
    if (!file) return;
    setReimbForm(prev => ({ ...prev, receipt: file, receipt_url: file.name }));
  };

  const handleAttachmentUpload = async (file) => {
    if (!file) return null;
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/api/rfi/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.attachment_url;
    } catch (error) {
      toast.error('Failed to upload attachment');
      return null;
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRfiSubmit = async (e) => {
    e.preventDefault();
    try {
      let attachmentUrl = null;
      if (rfiForm.attachment) {
        attachmentUrl = await handleAttachmentUpload(rfiForm.attachment);
      }
      
      const formData = {
        ...rfiForm,
        rfi_type: 'submission',
        attachment_url: attachmentUrl
      };
      delete formData.attachment;
      
      await axios.post(`${API_URL}/api/rfi`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      toast.success('RFI submitted!');
      setRfiForm({ title: '', description: '', project: '', attachment: null });
      loadData();
    } catch (error) {
      toast.error('Failed to submit RFI');
    }
  };

  const handleExportPdf = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rfi/export/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `RFI_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('RFI report exported!');
    } catch (error) {
      toast.error('Failed to export RFI report');
    }
  };

  const handlePhotoUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    // Client-side validation - expanded for mobile formats
    const ALLOWED_TYPES = [
      'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'image/heic', 'image/heif', 'image/tiff'
    ];
    const MAX_SIZE = 50 * 1024 * 1024; // 50MB for high-res mobile photos
    const MIN_SIZE = 500; // 500 bytes minimum
    
    const validFiles = [];
    const errors = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.toLowerCase().split('.').pop();
      
      // Check by extension as well (mobile browsers may not set correct MIME type for HEIC)
      const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic', 'heif', 'tiff', 'tif'];
      const isValidType = ALLOWED_TYPES.includes(file.type) || validExtensions.includes(ext);
      
      if (!isValidType) {
        errors.push(`${file.name}: Invalid file type`);
        continue;
      }
      
      if (file.size < MIN_SIZE) {
        errors.push(`${file.name}: File too small`);
        continue;
      }
      
      if (file.size > MAX_SIZE) {
        errors.push(`${file.name}: File too large (max 50MB)`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
    }
    
    if (validFiles.length === 0) {
      return;
    }
    
    setUploadingPhotos(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < validFiles.length; i++) {
        formData.append('files', validFiles[i]);
      }
      const response = await axios.post(`${API_URL}/api/dpr/upload-photos`, formData);
      if (response.data.photo_urls && response.data.photo_urls.length > 0) {
        setDprForm(prev => ({
          ...prev,
          site_photos: [...prev.site_photos, ...response.data.photo_urls]
        }));
        toast.success(`${response.data.photo_urls.length} photo(s) uploaded!`);
        
        if (response.data.errors && response.data.errors.length > 0) {
          response.data.errors.forEach(err => toast.warning(err));
        }
      } else {
        toast.error('No photos were uploaded');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload photos';
      toast.error(errorMsg);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDprSubmit = async (e) => {
    e.preventDefault();
    if (!dprForm.project) {
      toast.error('Please select a project');
      return;
    }
    setSubmittingDpr(true);
    try {
      // Use URLSearchParams for proper form encoding
      const formData = new URLSearchParams();
      formData.append('project', dprForm.project);
      formData.append('accomplished_works', dprForm.accomplished_works);
      formData.append('challenges', dprForm.challenges);
      formData.append('site_photos', JSON.stringify(dprForm.site_photos));
      
      console.log('Submitting DPR with photos:', dprForm.site_photos);
      
      await axios.post(`${API_URL}/api/dpr`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      toast.success('Daily Progress Report submitted! Design team and Admin have been notified.');
      setDprForm({ 
        project: '', 
        accomplished_works: '', 
        site_photos: [],
        challenges: '' 
      });
      loadData();
    } catch (error) {
      console.error('DPR submission error:', error);
      toast.error('Failed to submit DPR');
    } finally {
      setSubmittingDpr(false);
    }
  };

  const handleViewResponse = (rfi) => {
    // Navigate to Design Department RFI tab with response info
    navigate('/design?tab=rfi&responseId=' + rfi.response_id);
  };

  const handleExportDprPdf = async (dprId) => {
    try {
      const response = await axios.get(`${API_URL}/api/dpr/${dprId}/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DPR_${dprId.slice(0, 8)}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('DPR exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export DPR');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} theme={theme} onThemeToggle={onThemeToggle}>
      <div className="p-4 sm:p-8 space-y-6 sm:space-y-8" data-testid="construction-department-page">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent/20 rounded-md flex items-center justify-center">
            <Hammer className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Construction Department</h1>
            <p className="text-xs sm:text-base text-muted-foreground">Site workflows, RFI submissions, and daily progress reports</p>
          </div>
        </div>

        <Tabs scope="construction" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
            <TabsList className="bg-muted/50 flex-wrap h-auto gap-1 justify-start w-full sm:w-auto">
              <TabsTrigger value="overview" data-testid="tab-overview" className="whitespace-nowrap text-xs sm:text-sm">
                <LayoutDashboard className="w-4 h-4 mr-1" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="dtr" data-testid="tab-dtr" className="whitespace-nowrap text-xs sm:text-sm">DTR & Hours</TabsTrigger>
              <TabsTrigger value="reimbursement" data-testid="tab-reimbursement" className="whitespace-nowrap text-xs sm:text-sm">Reimburse</TabsTrigger>
              <TabsTrigger value="rfi" data-testid="tab-rfi" className="whitespace-nowrap text-xs sm:text-sm">RFI</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule" className="whitespace-nowrap text-xs sm:text-sm">Schedule</TabsTrigger>
              <TabsTrigger value="dpr" data-testid="tab-dpr" className="whitespace-nowrap text-xs sm:text-sm">DPR</TabsTrigger>
              <TabsTrigger value="references" data-testid="tab-references" className="whitespace-nowrap text-xs sm:text-sm">References</TabsTrigger>
              <TabsTrigger value="punchlist" data-testid="tab-punchlist" className="whitespace-nowrap text-xs sm:text-sm">
                <ClipboardList className="w-4 h-4 mr-1" />
                Punchlist
              </TabsTrigger>
              <TabsTrigger value="change-orders" data-testid="tab-change-orders" className="whitespace-nowrap text-xs sm:text-sm">
                <ClipboardList className="w-4 h-4 mr-1" />
                Change Orders
              </TabsTrigger>
              <TabsTrigger value="calendar" data-testid="tab-calendar" className="whitespace-nowrap text-xs sm:text-sm">
                <CalendarDays className="w-4 h-4 mr-1" />
                Calendar
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="space-y-6">
            <ProjectOverviewTab user={user} onJumpTab={setActiveTab} />
          </TabsContent>

          <TabsContent value="dtr" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="dtr-form-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-accent" />
                  <CardTitle>Log Site Hours</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleDtrSubmit} className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="project">Site / Project</Label>
                      <select
                        id="project"
                        data-testid="input-project"
                        className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                        value={dtrForm.project}
                        onChange={(e) => setDtrForm({ ...dtrForm, project: e.target.value })}
                        required
                      >
                        <option value="">Select Project</option>
                        {projects.map((proj) => (
                          <option key={proj.id} value={proj.project_name}>
                            {proj.project_name} ({proj.project_code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        type="date"
                        data-testid="input-date"
                        value={dtrForm.date}
                        onChange={(e) => handleDateChange(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Early Work Section */}
                  <div className="p-4 rounded-lg border border-purple-500/30 bg-purple-500/5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <Label className="text-purple-400 font-semibold">Early Work (12:00 AM - 7:59 AM)</Label>
                      <span className="text-xs text-muted-foreground ml-auto">Min. 1 hour required</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="early_in" className="text-sm">Early Time In</Label>
                        <Input
                          id="early_in"
                          type="time"
                          data-testid="input-early-in"
                          value={dtrForm.early_in}
                          onChange={(e) => setDtrForm({ ...dtrForm, early_in: e.target.value })}
                          min="00:00"
                          max="07:59"
                        />
                      </div>
                      <div>
                        <Label htmlFor="early_out" className="text-sm">Early Time Out</Label>
                        <Input
                          id="early_out"
                          type="time"
                          data-testid="input-early-out"
                          value={dtrForm.early_out}
                          onChange={(e) => setDtrForm({ ...dtrForm, early_out: e.target.value })}
                          min="00:00"
                          max="07:59"
                        />
                      </div>
                    </div>
                    {calculateEarlyHours() > 0 && (
                      <p className="text-xs text-purple-400 mt-2">Early work: {calculateEarlyHours().toFixed(1)} hours</p>
                    )}
                    {dtrForm.early_in && dtrForm.early_out && calculateEarlyHours() === 0 && (
                      <p className="text-xs text-orange-400 mt-2">Note: Less than 1 hour - will not count as early work</p>
                    )}
                  </div>

                  {/* Regular Work Section */}
                  <div className="p-4 rounded-lg border border-accent/30 bg-accent/5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-accent"></div>
                      <Label className="text-accent font-semibold">Regular Work Hours (8:00 AM - 5:00 PM)</Label>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="time_in" className="text-sm">Time In</Label>
                        <Input
                          id="time_in"
                          type="time"
                          data-testid="input-time-in"
                          value={dtrForm.time_in}
                          onChange={(e) => setDtrForm({ ...dtrForm, time_in: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="time_out" className="text-sm">Time Out</Label>
                        <Input
                          id="time_out"
                          type="time"
                          data-testid="input-time-out"
                          value={dtrForm.time_out}
                          onChange={(e) => setDtrForm({ ...dtrForm, time_out: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="break_hours" className="text-sm">Break Time (hours)</Label>
                        <Input
                          id="break_hours"
                          type="number"
                          step="0.5"
                          min="0"
                          max="3"
                          data-testid="input-break-hours"
                          value={dtrForm.break_hours}
                          onChange={(e) => setDtrForm({ ...dtrForm, break_hours: e.target.value })}
                          placeholder="1"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-accent mt-2">Regular work: {calculateRegularHours().toFixed(1)} hours (before break deduction)</p>
                  </div>

                  {/* Overtime Section */}
                  <div className="p-4 rounded-lg border border-orange-500/30 bg-orange-500/5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      <Label className="text-orange-400 font-semibold">Overtime (5:01 PM - 11:59 PM)</Label>
                      <span className="text-xs text-muted-foreground ml-auto">Min. 2 hours required</span>
                    </div>
                    
                    {/* OT Code Verification */}
                    <div className="mb-4 p-3 bg-background/50 rounded-lg border border-border/40">
                      <Label className="text-sm mb-2 block">OT Code</Label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Enter OT Code (e.g., OT-ABC123)"
                          data-testid="input-ot-code"
                          value={dtrForm.ot_code}
                          onChange={(e) => {
                            setDtrForm({ ...dtrForm, ot_code: e.target.value.toUpperCase() });
                            setOtCodeVerified(false);
                            setOtCodeMessage('');
                          }}
                          className="flex-1"
                        />
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={verifyOtCode}
                          disabled={verifyingOtCode || !dtrForm.ot_code.trim()}
                          data-testid="btn-verify-ot-code"
                        >
                          {verifyingOtCode ? 'Verifying...' : 'Verify'}
                        </Button>
                      </div>
                      {otCodeMessage && (
                        <p className={`text-xs mt-2 ${otCodeVerified ? 'text-green-500' : 'text-amber-500'}`}>
                          {otCodeMessage}
                        </p>
                      )}
                      {!otCodeVerified && (
                        <p className="text-xs text-muted-foreground mt-2">
                          ⚠️ OT application must be approved to get a code. Contact Admin if not yet approved.
                        </p>
                      )}
                      <div className="mt-3 border-t border-border/40 pt-2">
                        <button type="button" onClick={() => setShowPrevOt(v => !v)}
                          className="text-xs text-primary hover:underline" data-testid="btn-toggle-prev-ot">
                          {showPrevOt ? 'Hide previous OT applications' : 'Show previous OT applications'}
                        </button>
                        {showPrevOt && (
                          <div className="mt-2 max-h-48 overflow-y-auto space-y-1" data-testid="prev-ot-list">
                            {myOtApps.length === 0 && <p className="text-xs text-muted-foreground">No OT applications yet.</p>}
                            {myOtApps.map(ot => {
                              const usable = ot.status === 'approved' && ot.ot_code;
                              return (
                                <button key={ot.id} type="button"
                                  onClick={() => usable && selectPrevOt(ot)}
                                  disabled={!usable}
                                  className={`w-full text-left text-xs p-2 rounded border flex items-center justify-between gap-2 ${usable ? 'hover:bg-muted/50 border-border/40' : 'opacity-60 border-border/20 cursor-not-allowed'}`}
                                  data-testid={`prev-ot-${ot.id}`}>
                                  <span>{ot.date} · {ot.hours}h · <span className="capitalize">{ot.status}</span></span>
                                  {usable
                                    ? <span className="font-mono font-bold text-green-500">{ot.ot_code}</span>
                                    : <span className="text-muted-foreground">no code</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="overtime_in" className="text-sm">Overtime Start</Label>
                        <Input
                          id="overtime_in"
                          type="time"
                          data-testid="input-overtime-in"
                          value={dtrForm.overtime_in}
                          onChange={(e) => handleOvertimeStartChange(e.target.value)}
                          min="17:01"
                          max="23:59"
                          disabled={!otCodeVerified}
                          className={!otCodeVerified ? 'opacity-50 cursor-not-allowed' : ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="overtime_out" className="text-sm">Overtime End {approvedOtHours > 0 && <span className="text-muted-foreground">(auto-computed)</span>}</Label>
                        <Input
                          id="overtime_out"
                          type="time"
                          data-testid="input-overtime-out"
                          value={dtrForm.overtime_out}
                          onChange={(e) => setDtrForm({ ...dtrForm, overtime_out: e.target.value })}
                          min="17:01"
                          max="23:59"
                          disabled={!otCodeVerified}
                          className={!otCodeVerified ? 'opacity-50 cursor-not-allowed' : ''}
                        />
                      </div>
                    </div>
                    {calculateOvertimeHours() > 0 && (
                      <p className="text-xs text-orange-400 mt-2">Overtime: {calculateOvertimeHours().toFixed(1)} hours</p>
                    )}
                    {dtrForm.overtime_in && dtrForm.overtime_out && calculateOvertimeHours() === 0 && (
                      <p className="text-xs text-red-400 mt-2">Note: Less than 2 hours - will not count as overtime</p>
                    )}
                  </div>

                  {/* Hours Summary */}
                  <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <Label className="text-green-400 font-semibold">Hours Summary</Label>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                        <p className="text-2xl font-bold text-purple-400">{calculateEarlyHours().toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground">Early Work</p>
                      </div>
                      <div className="p-3 bg-accent/10 rounded-lg border border-accent/20">
                        <p className="text-2xl font-bold text-accent">{calculateRegularHours().toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground">Regular</p>
                      </div>
                      <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <p className="text-2xl font-bold text-red-400">-{parseFloat(dtrForm.break_hours || 0).toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground">Break</p>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <p className="text-2xl font-bold text-green-400">{calculateTotalWorkHours().toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground">Total Work</p>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20 text-center">
                      <p className="text-2xl font-bold text-orange-400">{calculateOvertimeHours().toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">Overtime Hours (Separate)</p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="work_description">Work Description</Label>
                    <Textarea
                      id="work_description"
                      data-testid="input-work-description"
                      value={dtrForm.work_description}
                      onChange={(e) => setDtrForm({ ...dtrForm, work_description: e.target.value })}
                      placeholder="Describe site work completed today..."
                      rows={3}
                      required
                    />
                  </div>
                  <Button type="submit" data-testid="btn-submit-dtr" className="w-full md:w-auto">Log DTR Entry</Button>
                </form>
              </CardContent>
            </Card>


            <Card className="glass-effect border-border/40" data-testid="dtr-entries-card">
              <CardHeader>
                <CardTitle>Your Site Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dtrEntries.map((entry) => (
                    <div key={entry.id} className="p-3 bg-muted/30 rounded-md" data-testid={`dtr-entry-${entry.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-foreground">{entry.project}</p>
                        <p className="text-sm font-mono text-accent">{entry.hours}h</p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-1">
                        <span>{entry.date}</span>
                        {entry.time_in && entry.time_out && (
                          <span className="font-mono">{entry.time_in} - {entry.time_out}</span>
                        )}
                      </div>
                      {entry.work_description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{entry.work_description}</p>
                      )}
                    </div>
                  ))}
                  {dtrEntries.length === 0 && <p className="text-muted-foreground text-sm">No entries yet</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reimbursement" className="space-y-6">
            {/* Gas Expense Calculator */}
            <Card className="glass-effect border-border/40" data-testid="gas-calculator-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <CardTitle>Gas Expense Calculator</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Calculate reimbursement for using your own vehicle</p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="const-gas-distance">Distance (km)</Label>
                    <Input
                      id="const-gas-distance"
                      type="number"
                      step="0.1"
                      min="0"
                      data-testid="input-gas-distance"
                      placeholder="e.g., 25"
                      className="bg-background/50"
                      onChange={(e) => {
                        const distanceInput = document.getElementById('const-gas-distance');
                        const priceInput = document.getElementById('const-gas-price');
                        const resultDiv = document.getElementById('const-gas-result');
                        const distance = parseFloat(distanceInput.value) || 0;
                        const price = parseFloat(priceInput.value) || 0;
                        const total = (distance / 10) * price;
                        resultDiv.textContent = total > 0 ? `₱${total.toFixed(2)}` : '₱0.00';
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="const-gas-price">Current Gas Price (₱/L)</Label>
                    <Input
                      id="const-gas-price"
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="input-gas-price"
                      placeholder="e.g., 65.50"
                      className="bg-background/50"
                      onChange={(e) => {
                        const distanceInput = document.getElementById('const-gas-distance');
                        const priceInput = document.getElementById('const-gas-price');
                        const resultDiv = document.getElementById('const-gas-result');
                        const distance = parseFloat(distanceInput.value) || 0;
                        const price = parseFloat(priceInput.value) || 0;
                        const total = (distance / 10) * price;
                        resultDiv.textContent = total > 0 ? `₱${total.toFixed(2)}` : '₱0.00';
                      }}
                    />
                  </div>
                  <div>
                    <Label>Computed Amount</Label>
                    <div className="h-11 flex items-center px-3 rounded-md border border-input bg-accent/10">
                      <span id="const-gas-result" className="text-lg font-bold text-accent" data-testid="gas-result">₱0.00</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Formula: (Distance ÷ 10) × Gas Price — Use this computed amount for your reimbursement request below.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="reimbursement-form-card">
              <CardHeader>
                <CardTitle>Submit Reimbursement Request</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleReimbursementSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="amount">Amount (PHP)</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        data-testid="input-amount"
                        value={reimbForm.amount}
                        onChange={(e) => setReimbForm({ ...reimbForm, amount: e.target.value })}
                        placeholder="500.00"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="receipt">Receipt Compilation (Required)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="receipt"
                          type="file"
                          accept="image/*,.pdf"
                          data-testid="input-receipt"
                          onChange={(e) => handleReceiptSelect(e.target.files[0])}
                          className="flex-1"
                        />
                      </div>
                      {reimbForm.receipt && (
                        <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          {reimbForm.receipt.name} selected
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="reimb-project">Project (Required)</Label>
                      <select
                        id="reimb-project"
                        data-testid="select-reimb-project"
                        value={reimbForm.project_id}
                        onChange={(e) => {
                          const p = projects.find(pr => pr.id === e.target.value);
                          setReimbForm({ ...reimbForm, project_id: e.target.value, project_name: p ? p.project_name : '' });
                        }}
                        required
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="">Select project...</option>
                        {projects.map(p => (
                          <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="reimb-date">Date</Label>
                      <Input
                        id="reimb-date"
                        type="date"
                        data-testid="input-reimb-date"
                        value={reimbForm.date}
                        onChange={(e) => setReimbForm({ ...reimbForm, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      data-testid="input-reimb-description"
                      value={reimbForm.description}
                      onChange={(e) => setReimbForm({ ...reimbForm, description: e.target.value })}
                      placeholder="Emergency materials, fuel, toll fees..."
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={!reimbForm.receipt}
                    data-testid="btn-submit-reimbursement"
                  >
                    Submit Request
                  </Button>
                  {!reimbForm.receipt && (
                    <p className="text-xs text-muted-foreground">Select receipt file to enable submission</p>
                  )}
                </form>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="reimbursement-list-card">
              <CardHeader>
                <CardTitle>Your Reimbursement Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {reimbursements.map((req) => (
                    <div key={req.id} className="p-3 bg-muted/30 rounded-md" data-testid={`reimb-${req.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-foreground">{req.description}</p>
                        <p className="text-sm font-mono text-accent">₱{req.amount}</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs flex-wrap">
                        <span className={`px-2 py-0.5 rounded capitalize font-medium ${
                          req.status === 'paid' ? 'bg-emerald-500/25 text-emerald-600' :
                          req.status === 'for_payment' ? 'bg-blue-500/25 text-blue-600' :
                          req.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          req.status === 'rejected' || req.status === 'declined' ? 'bg-red-500/20 text-red-500' :
                          'bg-accent/20 text-accent'
                        }`}>
                          {req.status === 'for_payment' ? 'For Payment' : req.status}
                        </span>
                        {req.receipt_url && (
                          <a 
                            href={`${API_URL}${req.receipt_url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            <Paperclip className="w-3 h-3" />
                            Receipt
                          </a>
                        )}
                        {(req.status === 'for_payment' || req.status === 'paid') && (
                          <button
                            onClick={async () => {
                              try {
                                const response = await axios.get(`${API_URL}/api/reimbursement/${req.id}/export`, { responseType: 'blob' });
                                const blob = new Blob([response.data], { type: 'application/pdf' });
                                const u = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = u;
                                link.download = `Reimbursement_${req.id.slice(0,8)}_${new Date().toISOString().split('T')[0]}.pdf`;
                                document.body.appendChild(link); link.click(); link.remove();
                                window.URL.revokeObjectURL(u);
                                toast.success('PDF downloaded');
                              } catch { toast.error('Download failed'); }
                            }}
                            className="text-primary hover:underline flex items-center gap-1 font-medium"
                            data-testid={`btn-download-reimb-pdf-${req.id}`}
                          >
                            <Download className="w-3 h-3" />
                            Download PDF
                          </button>
                        )}
                        <span className="text-muted-foreground">
                          {new Date(req.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                  {reimbursements.length === 0 && <p className="text-muted-foreground text-sm">No requests yet</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rfi" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="rfi-form-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-accent" />
                    <CardTitle>Submit Site RFI</CardTitle>
                  </div>
                  <Button variant="outline" onClick={handleExportPdf} data-testid="btn-export-rfi-pdf">
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRfiSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="rfi_title">Issue Title</Label>
                      <Input
                        id="rfi_title"
                        data-testid="input-rfi-title"
                        value={rfiForm.title}
                        onChange={(e) => setRfiForm({ ...rfiForm, title: e.target.value })}
                        placeholder="Drawing discrepancy - Foundation Level"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="rfi_project">Project</Label>
                      <select
                        id="rfi_project"
                        data-testid="input-rfi-project"
                        className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                        value={rfiForm.project}
                        onChange={(e) => setRfiForm({ ...rfiForm, project: e.target.value })}
                        required
                      >
                        <option value="">Select Project</option>
                        {projects.map((proj) => (
                          <option key={proj.id} value={proj.project_name}>
                            {proj.project_name} ({proj.project_code})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="rfi_description">Issue Description</Label>
                    <Textarea
                      id="rfi_description"
                      rows={5}
                      data-testid="input-rfi-description"
                      value={rfiForm.description}
                      onChange={(e) => setRfiForm({ ...rfiForm, description: e.target.value })}
                      placeholder="Describe the site condition, discrepancy, or clarification needed..."
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="rfi_attachment">Attachment (Optional)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="rfi_attachment"
                        type="file"
                        data-testid="input-rfi-attachment"
                        onChange={(e) => setRfiForm({ ...rfiForm, attachment: e.target.files[0] })}
                        className="flex-1"
                      />
                      {rfiForm.attachment && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          {rfiForm.attachment.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button type="submit" disabled={uploadingAttachment} data-testid="btn-submit-rfi">
                    {uploadingAttachment ? 'Uploading...' : 'Submit RFI'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="rfi-list-card">
              <CardHeader>
                <CardTitle>Submitted RFIs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {rfis.map((rfi) => (
                    <div key={rfi.id} className="p-4 bg-muted/30 rounded-md" data-testid={`rfi-${rfi.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-foreground">{rfi.title}</p>
                          {rfi.project && (
                            <p className="text-xs text-muted-foreground">Project: {rfi.project}</p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded capitalize ${
                          rfi.status === 'responded' 
                            ? 'bg-green-500/20 text-green-500' 
                            : 'bg-accent/20 text-accent'
                        }`}>
                          {rfi.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{rfi.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {rfi.attachment_url && (
                            <a 
                              href={`${API_URL}${rfi.attachment_url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-accent hover:underline"
                            >
                              <Paperclip className="w-3 h-3" />
                              Attachment
                            </a>
                          )}
                          <span>{new Date(rfi.created_at).toLocaleDateString()}</span>
                        </div>
                        {rfi.status === 'responded' && rfi.response_id && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewResponse(rfi)}
                            data-testid={`btn-view-response-${rfi.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Response
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {rfis.length === 0 && <p className="text-muted-foreground text-sm">No RFIs submitted yet</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule">
            <ScheduleTab department="Construction" user={user} />
          </TabsContent>

          <TabsContent value="dpr" className="space-y-6">
            {/* DPR Submission Form */}
            <Card className="glass-effect border-border/40" data-testid="dpr-form-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-accent" />
                  <CardTitle>Submit Daily Progress Report</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleDprSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dpr_project">Project Name</Label>
                      <select
                        id="dpr_project"
                        data-testid="input-dpr-project"
                        className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                        value={dprForm.project}
                        onChange={(e) => setDprForm({ ...dprForm, project: e.target.value })}
                        required
                      >
                        <option value="">Select Project</option>
                        {projects.map((proj) => (
                          <option key={proj.id} value={proj.project_name}>
                            {proj.project_name} ({proj.project_code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Site Photos</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="file"
                          multiple
                          accept="image/*"
                          data-testid="input-dpr-photos"
                          onChange={(e) => handlePhotoUpload(e.target.files)}
                          disabled={uploadingPhotos}
                          className="flex-1"
                        />
                        {uploadingPhotos && <span className="text-sm text-accent animate-pulse">Uploading...</span>}
                        {dprForm.site_photos.length > 0 && (
                          <span className="text-sm text-green-500 font-medium">{dprForm.site_photos.length} photo(s)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="accomplished_works">Accomplished Works Report</Label>
                    <Textarea
                      id="accomplished_works"
                      rows={4}
                      data-testid="input-dpr-accomplished"
                      value={dprForm.accomplished_works}
                      onChange={(e) => setDprForm({ ...dprForm, accomplished_works: e.target.value.slice(0, 2000) })}
                      placeholder="Describe all work accomplished today..."
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="challenges">Challenges / Urgent Items (Optional)</Label>
                    <Textarea
                      id="challenges"
                      rows={2}
                      data-testid="input-dpr-challenges"
                      value={dprForm.challenges}
                      onChange={(e) => setDprForm({ ...dprForm, challenges: e.target.value.slice(0, 500) })}
                      placeholder="List any challenges or urgent items..."
                    />
                  </div>

                  {/* Photo Preview */}
                  {dprForm.site_photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg">
                      {dprForm.site_photos.map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={`${API_URL}${photo}`} 
                            alt={`Photo ${idx + 1}`}
                            className="w-16 h-16 object-cover rounded border border-border"
                          />
                          <button
                            type="button"
                            onClick={() => setDprForm(prev => ({
                              ...prev,
                              site_photos: prev.site_photos.filter((_, i) => i !== idx)
                            }))}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button type="submit" disabled={submittingDpr || uploadingPhotos} data-testid="btn-submit-dpr">
                    <Upload className="w-4 h-4 mr-2" />
                    {submittingDpr ? 'Submitting...' : 'Submit DPR'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* DPR List */}
            <Card className="glass-effect border-border/40" data-testid="dpr-list-card">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Submitted Daily Progress Reports
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <select
                      value={dprProjectFilter}
                      onChange={(e) => setDprProjectFilter(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background/50 px-3 text-sm min-w-[200px]"
                      data-testid="dpr-project-filter"
                    >
                      <option value="">All Projects</option>
                      {[...new Set(dprs.map(d => d.project))].sort().map(proj => (
                        <option key={proj} value={proj}>{proj}</option>
                      ))}
                    </select>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {dprProjectFilter 
                        ? `${dprs.filter(d => d.project === dprProjectFilter).length} of ${dprs.length} report(s)`
                        : `${dprs.length} report(s)`
                      }
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dprs.filter(d => !dprProjectFilter || d.project === dprProjectFilter).length === 0 ? (
                    <p className="text-muted-foreground text-sm text-center py-8">
                      {dprProjectFilter ? 'No reports found for this project.' : 'No daily progress reports submitted yet.'}
                    </p>
                  ) : (
                    dprs.filter(d => !dprProjectFilter || d.project === dprProjectFilter).map((dpr) => (
                      <div 
                        key={dpr.id} 
                        className="p-4 bg-muted/30 rounded-lg border border-border/40 hover:border-primary/30 transition-colors"
                        data-testid={`dpr-item-${dpr.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          {/* Left side - Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-foreground truncate">{dpr.project}</h3>
                              {dpr.site_photos && dpr.site_photos.length > 0 && (
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-500 text-xs rounded-full flex items-center gap-1">
                                  <Camera className="w-3 h-3" />
                                  {dpr.site_photos.length} photo(s)
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              <span className="font-medium">Submitted:</span> {new Date(dpr.created_at).toLocaleDateString('en-US', { 
                                year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                              })}
                              <span className="mx-2">•</span>
                              <span className="font-medium">By:</span> {dpr.submitted_by_name}
                            </p>
                            <p className="text-sm text-muted-foreground line-clamp-2">{dpr.accomplished_works}</p>
                            {dpr.challenges && (
                              <p className="text-xs text-orange-500 mt-1">
                                <span className="font-medium">Challenges:</span> {dpr.challenges.slice(0, 80)}...
                              </p>
                            )}
                          </div>

                          {/* Right side - Photos Preview & Actions */}
                          <div className="flex flex-col items-end gap-2">
                            {/* Photo thumbnails */}
                            {dpr.site_photos && dpr.site_photos.length > 0 && (
                              <div className="flex gap-1">
                                {dpr.site_photos.slice(0, 3).map((photo, idx) => (
                                  <img
                                    key={idx}
                                    src={`${API_URL}${photo}`}
                                    alt={`Photo ${idx + 1}`}
                                    className="w-12 h-12 object-cover rounded border border-border"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ))}
                                {dpr.site_photos.length > 3 && (
                                  <div className="w-12 h-12 bg-muted rounded border border-border flex items-center justify-center text-xs text-muted-foreground">
                                    +{dpr.site_photos.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Action buttons */}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedDpr(dpr)}
                                data-testid={`btn-view-dpr-${dpr.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleExportDprPdf(dpr.id)}
                                data-testid={`btn-export-dpr-${dpr.id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Export PDF
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* DPR Detail Modal */}
            {selectedDpr && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedDpr(null)}>
                <div 
                  className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold">Daily Progress Report</h2>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => handleExportDprPdf(selectedDpr.id)}>
                        <Download className="w-4 h-4 mr-1" />
                        Export PDF
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedDpr(null)}>✕</Button>
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Header Info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Project</p>
                        <p className="font-semibold text-lg">{selectedDpr.project}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Date Submitted</p>
                        <p className="font-semibold">{new Date(selectedDpr.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                        })}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Submitted By</p>
                        <p className="font-semibold">{selectedDpr.submitted_by_name}</p>
                      </div>
                    </div>

                    {/* Accomplished Works */}
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Accomplished Works</h3>
                      <div className="p-4 bg-muted/30 rounded-lg whitespace-pre-wrap text-sm">
                        {selectedDpr.accomplished_works}
                      </div>
                    </div>

                    {/* Challenges */}
                    {selectedDpr.challenges && (
                      <div>
                        <h3 className="font-semibold text-orange-500 mb-2">Challenges / Urgent Items</h3>
                        <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg whitespace-pre-wrap text-sm">
                          {selectedDpr.challenges}
                        </div>
                      </div>
                    )}

                    {/* Site Photos */}
                    {selectedDpr.site_photos && selectedDpr.site_photos.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-green-500 mb-2 flex items-center gap-2">
                          <Camera className="w-4 h-4" />
                          Site Photos ({selectedDpr.site_photos.length})
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {selectedDpr.site_photos.map((photo, idx) => (
                            <div key={idx} className="relative group">
                              <img
                                src={`${API_URL}${photo}`}
                                alt={`Site Photo ${idx + 1}`}
                                className="w-full h-48 object-cover rounded-lg border border-border"
                                onError={(e) => { 
                                  e.target.src = ''; 
                                  e.target.className = 'w-full h-48 bg-muted rounded-lg border border-border flex items-center justify-center';
                                  e.target.alt = 'Failed to load';
                                }}
                              />
                              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 text-white text-xs rounded">
                                Photo {idx + 1}
                              </div>
                              <a
                                href={`${API_URL}${photo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="absolute top-2 right-2 p-1 bg-black/60 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* References Tab - View construction drawing links */}
          <TabsContent value="references" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="references-card">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <Link className="w-5 h-5 text-blue-500" />
                    <CardTitle>Construction Drawing References</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Filter by Project:</Label>
                    <select
                      value={refProjectFilter}
                      onChange={(e) => setRefProjectFilter(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background/50 px-3 text-sm min-w-[200px]"
                      data-testid="ref-project-filter"
                    >
                      <option value="">All Projects</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.project_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Latest construction drawings and reference links managed by the Design team.
                </p>
                
                {constructionReferences.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Link className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No reference links available yet</p>
                    <p className="text-sm">The Design team will add drawing references here.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {constructionReferences
                      .filter(ref => !refProjectFilter || ref.project_id === refProjectFilter)
                      .map(ref => (
                        <div key={ref.id || ref.project_id} className="border border-border/40 rounded-lg p-4">
                          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                            <FolderOpen className="w-5 h-5 text-accent" />
                            {ref.project_name}
                          </h3>
                          
                          {ref.drawing_links && ref.drawing_links.length > 0 ? (
                            <div className="space-y-2">
                              {ref.drawing_links.map(link => (
                                <a
                                  key={link.id}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 rounded-md transition-colors group"
                                  data-testid={`ref-link-${link.id}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <ExternalLink className="w-4 h-4 text-blue-500" />
                                    <div>
                                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">{link.title}</p>
                                      {link.description && (
                                        <p className="text-xs text-muted-foreground">{link.description}</p>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {link.updated_at && new Date(link.updated_at).toLocaleDateString()}
                                  </span>
                                </a>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">No drawing links for this project yet.</p>
                          )}
                        </div>
                      ))
                    }
                    
                    {constructionReferences.filter(ref => !refProjectFilter || ref.project_id === refProjectFilter).length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No references found for the selected project.</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Punchlist Tab (Full access — same as Design dept) */}
          <TabsContent value="punchlist" className="space-y-6">
            <PunchlistTab user={user} projects={projects} isReadOnly={false} />
          </TabsContent>

          {/* Change Orders Tab — Construction mode */}
          <TabsContent value="change-orders" className="space-y-6">
            <ChangeOrdersTab mode="construction" />
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-6">
            <CalendarTab user={user} projects={projects} department="Construction" />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}